/**
 * @fileoverview A component that renders a realistic-looking preview of a social media post
 * for different platforms like Facebook, X, and Instagram.
 */
import React from 'react';
import { Platform, type MediaType } from '../types';
import { GlobeIcon, HeartIcon, ChatBubbleIcon, ArrowPathRoundedSquareIcon, ArrowUpTrayIcon, EllipsisHorizontalIcon } from './icons';

interface PlatformPreviewProps {
  platform: Platform;
  content: string;
  mediaUrl: string | null;
  mediaType: MediaType | null;
}

/**
 * Parses text content and applies platform-specific formatting for a realistic preview.
 * @param text The input string to format.
 * @param platform The target social media platform.
 * @returns A ReactNode with formatted text, applying styles for hashtags on X and mentions on Instagram.
 */
const formatPlatformText = (text: string, platform: Platform): React.ReactNode => {
    if (!text) return text;
  
    // Regex to split text by hashtags and mentions, keeping the delimiters.
    const parts = text.split(/(#\w+|@\w+)/g);
  
    return parts.map((part, index) => {
      const isHashtag = part.startsWith('#');
      const isMention = part.startsWith('@');
      const isRelevantPlatform = [Platform.X, Platform.Threads, Platform.Bluesky, Platform.Instagram, Platform.Mastodon, Platform.TikTok].includes(platform);

      if (isRelevantPlatform && (isHashtag || isMention)) {
        return <strong key={index} className="font-semibold text-blue-600 dark:text-blue-400">{part}</strong>;
      }
      // Return plain text parts as they are.
      return part;
    });
};


/**
 * A reusable component for displaying a user avatar and name pill.
 */
const UserPill: React.FC<{ children: React.ReactNode, subtext?: string, subIcon?: React.ReactNode }> = ({ children, subtext, subIcon }) => (
    <div className="flex items-center gap-3">
        {/* Placeholder for user avatar */}
        <div className="w-10 h-10 rounded-full bg-slate-300 dark:bg-gray-600 flex-shrink-0"></div>
        <div>
            <p className="font-bold text-sm text-slate-800 dark:text-gray-100">{children}</p>
            {(subtext || subIcon) && 
              <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-gray-400">
                <span>{subtext}</span>
                {subIcon}
              </div>
            }
        </div>
    </div>
);

/**
 * A reusable component for displaying media previews.
 */
const MediaPreview: React.FC<{mediaUrl: string | null, mediaType: MediaType | null, className?: string}> = ({ mediaUrl, mediaType, className = '' }) => {
    if (!mediaUrl) return null;
    return (
        <div className={`mt-3 rounded-lg overflow-hidden bg-slate-200 dark:bg-gray-700 flex items-center justify-center ${className}`}>
             {mediaType === 'image' ? (
                <img src={mediaUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
                <video src={mediaUrl} className="w-full h-full object-cover" muted loop playsInline autoPlay />
            )}
        </div>
    )
};

/**
 * Renders a preview that mimics a Facebook post.
 */
const FacebookPreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    return (
        <div className="w-full p-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-slate-200 dark:border-gray-700">
            <div className="flex justify-between items-start">
              <UserPill subtext="Just now" subIcon={<GlobeIcon className="w-3 h-3" />}>AmplifyChat</UserPill>
              <EllipsisHorizontalIcon className="w-5 h-5 text-slate-500 dark:text-gray-400" />
            </div>
            <p className="my-3 text-sm text-slate-800 dark:text-gray-200 whitespace-pre-wrap break-words">{content || 'Your post content will appear here.'}</p>
            <MediaPreview mediaUrl={mediaUrl} mediaType={mediaType} />
            <div className="mt-3 pt-2 border-t border-slate-200 dark:border-gray-700 flex justify-around text-slate-600 dark:text-gray-400">
                {/* Mock action buttons */}
                <button className="flex items-center gap-2 font-semibold text-sm hover:bg-slate-100 dark:hover:bg-gray-700/50 p-2 rounded-md w-full justify-center">
                    <HeartIcon className="w-5 h-5" /> Like
                </button>
                <button className="flex items-center gap-2 font-semibold text-sm hover:bg-slate-100 dark:hover:bg-gray-700/50 p-2 rounded-md w-full justify-center">
                    <ChatBubbleIcon className="w-5 h-5" /> Comment
                </button>
                <button className="flex items-center gap-2 font-semibold text-sm hover:bg-slate-100 dark:hover:bg-gray-700/50 p-2 rounded-md w-full justify-center">
                    <ArrowUpTrayIcon className="w-5 h-5" /> Share
                </button>
            </div>
        </div>
    );
};

/**
 * Renders a preview that mimics an X (Twitter) post, with hashtag formatting.
 */
const XPreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    const charLimit = 280;
    const isExceeded = content.length > charLimit;

    // Apply formatting only to the content that is within the character limit.
    const visibleContent = content.substring(0, charLimit);
    const exceededContent = content.substring(charLimit);
    const formattedContent = formatPlatformText(visibleContent, Platform.X);

    return (
        <div className="w-full p-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-slate-200 dark:border-gray-700 flex gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-300 dark:bg-gray-600 flex-shrink-0"></div>
            <div className="flex-grow">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-slate-800 dark:text-gray-100">AmplifyChat</span>
                        <span className="text-slate-500 dark:text-gray-400 text-sm">@AmplifyChat</span>
                    </div>
                     <EllipsisHorizontalIcon className="w-5 h-5 text-slate-500 dark:text-gray-400" />
                </div>
                <p className="my-1 text-sm text-slate-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                  {content.length > 0 ? (
                      <>
                          {formattedContent}
                          {isExceeded && <span className="bg-red-200 dark:bg-red-800/50 text-red-600 dark:text-red-400">{exceededContent}</span>}
                      </>
                  ) : 'Your post content will appear here.'}
                </p>
                <MediaPreview mediaUrl={mediaUrl} mediaType={mediaType} className="border border-slate-200 dark:border-gray-700" />
                <div className="mt-3 flex justify-between text-slate-500 dark:text-gray-400 max-w-xs">
                    {/* Mock action buttons */}
                    <button className="flex items-center gap-2 hover:text-blue-500"><ChatBubbleIcon className="w-4 h-4" /> <span className="text-xs">12</span></button>
                    <button className="flex items-center gap-2 hover:text-green-500"><ArrowPathRoundedSquareIcon className="w-4 h-4" /> <span className="text-xs">5</span></button>
                    <button className="flex items-center gap-2 hover:text-pink-500"><HeartIcon className="w-4 h-4" /> <span className="text-xs">150</span></button>
                    <button className="flex items-center gap-2 hover:text-blue-500"><ArrowUpTrayIcon className="w-4 h-4" /></button>
                </div>
            </div>
        </div>
    );
};

/**
 * Renders a preview that mimics an Instagram post, with mention formatting.
 */
const InstagramPreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    const formattedContent = formatPlatformText(content, Platform.Instagram);

    return (
        <div className="w-full rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-slate-200 dark:border-gray-700">
            <div className="p-3 flex justify-between items-center">
                 <UserPill>amplifychat</UserPill>
                 <EllipsisHorizontalIcon className="w-5 h-5 text-slate-500 dark:text-gray-400" />
            </div>
            <div className="aspect-square bg-slate-200 dark:bg-gray-700 flex items-center justify-center">
                 {mediaUrl ? (
                    <MediaPreview mediaUrl={mediaUrl} mediaType={mediaType} className="mt-0 w-full h-full" />
                 ) : (
                    <span className="text-slate-500 dark:text-gray-400 text-sm">Media required</span>
                 )}
            </div>
            <div className="p-3">
                <div className="flex items-center gap-4 text-slate-800 dark:text-gray-200">
                    <HeartIcon className="w-6 h-6" />
                    <ChatBubbleIcon className="w-6 h-6" />
                    <ArrowUpTrayIcon className="w-6 h-6" />
                </div>
                <p className="text-sm mt-2 font-semibold">1,234 likes</p>
                <p className="mt-2 text-sm text-slate-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                    <span className="font-bold">amplifychat</span>{' '}
                    {content ? formattedContent : 'Your post content will appear here.'}
                </p>
            </div>
        </div>
    );
};

/**
 * Renders a preview that mimics a YouTube post.
 */
const YouTubePreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    return (
        <div className="w-full rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-slate-200 dark:border-gray-700">
            <div className="aspect-video bg-slate-200 dark:bg-gray-700 flex items-center justify-center">
                {mediaUrl && mediaType === 'video' ? (
                    <video src={mediaUrl} className="w-full h-full object-cover" controls />
                ) : (
                    <span className="text-slate-500 dark:text-gray-400 text-sm px-4 text-center">A video is required for YouTube</span>
                )}
            </div>
            <div className="p-3">
                <h3 className="font-bold text-slate-800 dark:text-gray-100 line-clamp-2">{content || 'Your video title will appear here.'}</h3>
                <p className="text-sm text-slate-600 dark:text-gray-400 mt-2">AmplifyChat</p>
                <p className="text-xs text-slate-500 dark:text-gray-400">1.2K views &bull; Just now</p>
            </div>
        </div>
    );
};

const ThreadsPreview: React.FC<PlatformPreviewProps> = (props) => (
    <XPreview {...props} />
);

const BlueskyPreview: React.FC<PlatformPreviewProps> = (props) => (
    <XPreview {...props} />
);

const MastodonPreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    const formattedContent = formatPlatformText(content, Platform.Mastodon);
    return (
        <div className="w-full p-4 rounded-lg bg-white dark:bg-gray-800 shadow-sm border border-slate-200 dark:border-gray-700">
            <UserPill subtext="amplifychat@mastodon.social">AmplifyChat</UserPill>
            <p className="my-3 text-sm text-slate-800 dark:text-gray-200 whitespace-pre-wrap break-words">{content ? formattedContent : 'Your post content will appear here.'}</p>
            <MediaPreview mediaUrl={mediaUrl} mediaType={mediaType} />
            <div className="mt-3 flex justify-between text-slate-500 dark:text-gray-400 max-w-sm">
                <button className="flex items-center gap-2 hover:text-blue-500"><ChatBubbleIcon className="w-5 h-5" /></button>
                <button className="flex items-center gap-2 hover:text-green-500"><ArrowPathRoundedSquareIcon className="w-5 h-5" /></button>
                <button className="flex items-center gap-2 hover:text-yellow-500"><HeartIcon className="w-5 h-5" /></button>
                <button className="flex items-center gap-2 hover:text-blue-500"><ArrowUpTrayIcon className="w-5 h-5" /></button>
            </div>
        </div>
    );
};

const TikTokPreview: React.FC<PlatformPreviewProps> = ({ content, mediaUrl, mediaType }) => {
    const formattedContent = formatPlatformText(content, Platform.TikTok);
    return (
        <div className="w-full max-w-[250px] mx-auto aspect-[9/16] rounded-xl bg-black shadow-sm border-2 border-slate-400 dark:border-gray-600 relative overflow-hidden flex items-center justify-center">
             {mediaUrl && mediaType === 'video' ? (
                <video src={mediaUrl} className="w-full h-full object-cover" muted loop playsInline autoPlay />
             ) : (
                <span className="text-slate-200 dark:text-gray-300 text-sm px-4 text-center">A video is required for TikTok posts</span>
             )}
             <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white">
                <p className="font-bold text-sm">@AmplifyChat</p>
                <p className="text-xs mt-1 whitespace-pre-wrap break-words">{content ? formattedContent : 'Your video description will appear here.'}</p>
             </div>
        </div>
    );
}

/**
 * The main preview component that selects the correct preview based on the platform prop.
 */
export const PlatformPreview: React.FC<PlatformPreviewProps> = (props) => {
    const { platform } = props;

    switch (platform) {
        case Platform.Facebook:
            return <FacebookPreview {...props} />;
        case Platform.X:
            return <XPreview {...props} />;
        case Platform.Instagram:
            return <InstagramPreview {...props} />;
        case Platform.YouTube:
            return <YouTubePreview {...props} />;
        case Platform.Threads:
            return <ThreadsPreview {...props} />;
        case Platform.Bluesky:
            return <BlueskyPreview {...props} />;
        case Platform.Mastodon:
            return <MastodonPreview {...props} />;
        case Platform.TikTok:
            return <TikTokPreview {...props} />;
        default:
            // Fallback for platforms without a specific preview component.
            return (
                <div className="text-center p-4 h-96 flex items-center justify-center bg-slate-100 dark:bg-gray-800 rounded-lg">
                    <p className="text-slate-500 dark:text-gray-400">Preview for {platform} is not available.</p>
                </div>
            );
    }
};